import React from "react";
import { useTranslation } from "react-i18next";
import AdvTaskListingDto from "../../../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingDto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faComment,
  faTag,
} from "@fortawesome/pro-regular-svg-icons";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons";
import dateHelper from "../../../../helpers/dateHelper";
import { listingHelper } from "../../../../helpers/listingHelper";
import { ClientTaskType } from "../../../../types/dtos/tasks/advanced-tasks/ClientTaskType";
import { advancedTaskHelper } from "../../../../helpers";

interface AdvTaskUserTaskItemProps {
  task: AdvTaskListingDto;
  taskType?: ClientTaskType | null;
  onTaskClick: (taskId: string) => void;
}

const AdvTaskUserTaskItem: React.FC<AdvTaskUserTaskItemProps> = ({
  task,
  taskType,
  onTaskClick,
}) => {
  const { t } = useTranslation();

  const formattedTargetDate = task.targetDate
    ? dateHelper
        .storedTargetDateAsLocalCalendarDate(task.targetDate)
        .toLocaleDateString()
    : "Not available";

  const formattedCompletionDate = task.dateCompleted
    ? dateHelper.convertUtcDateToLocal(task.dateCompleted).toLocaleDateString()
    : "Not available";

  const statusText = listingHelper.getTaskStatusText(task);
  const statusColor = listingHelper.getTaskStatusColor(task);

  const getTaskTypeName = (toLowerCase: boolean): string => {
    if (taskType && taskType.singularNameTranslationKeyIdentifier) {
      return toLowerCase
        ? advancedTaskHelper.ToLowerCase(
            taskType.singularNameTranslationKeyIdentifier,
          )
        : t(taskType.singularNameTranslationKeyIdentifier);
    }
    return t("Common.Task");
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent running twice (parent div has onClick as well as button)
    onTaskClick(task.userTaskId);
  };

  const renderMainContent = () => {
    return (
      <div
        onClick={handleButtonClick}
        className={`flex flex-col sm:flex-row justify-between w-full py-3 px-4 relative cursor-pointer ${listingHelper.getManagerBorderClass(
          task,
        )}`}
      >
        <div className="flex flex-col items-start flex-grow mb-2 sm:mb-0">
          <div className="flex items-center">
            <span className="font-medium text-base text-gray-700 mr-2 break-words">
              {task.title}
            </span>
          </div>

          <div
            className="flex flex-col sm:flex-row sm:space-x-4 mt-1 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center text-sm text-gray-500 mb-1 sm:mb-0">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="mr-1 text-gray-500"
                size="xs"
              />
              <span>Due: {formattedTargetDate}</span>
            </div>

            {task.categoryName && (
              <div className="flex items-center text-sm text-gray-500 mb-1 sm:mb-0">
                <FontAwesomeIcon
                  icon={faTag}
                  className="mr-1 text-gray-500"
                  size="xs"
                />
                <span>{t(task.categoryName)}</span>
              </div>
            )}

            {task.commentCount > 0 && (
              <div className="flex items-center text-sm text-gray-500">
                <FontAwesomeIcon
                  icon={faComment}
                  className="mr-1 text-gray-500"
                  size="xs"
                />
                <span>Comments: {task.commentCount}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto">
          <span
            className={`text-xs px-2 py-0.5 rounded-full mr-2 sm:mr-4 cursor-default ${statusColor}`}
            onClick={(e) => e.stopPropagation()}
          >
            {statusText}
          </span>

          <button
            className="btn-primary text-sm py-1.5 px-2 sm:px-4 min-w-[68px] sm:w-24 text-center whitespace-nowrap overflow-visible"
            onClick={handleButtonClick}
          >
            {t("Common.View")}
          </button>
        </div>
      </div>
    );
  };

  // Render completed section (only when task is completed)
  const renderCompletedSection = () => {
    if (task.taskStatus !== "COMPLETED") {
      return null;
    }

    return (
      <div className="border-t border-gray-200 bg-white px-3 sm:px-4 py-2 text-sm text-gray-500">
        <div className="flex items-center">
          <span className="flex items-center">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="mr-1 text-green-600"
              size="sm"
            />
            Completed{" "}
            {task.completedByUserFullName
              ? `by ${task.completedByUserFullName} `
              : ""}
            on {formattedCompletionDate}
          </span>
          {task.reviewedInAnswerSetName && (
            <>
              <span className="mx-2 text-gray-300">|</span>
              <span>Reviewed in {task.reviewedInAnswerSetName}</span>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="mb-3 border-[0.5px] border-gray-300 rounded-md bg-gray-50 hover:bg-gray-100/90 overflow-hidden">
      {renderMainContent()}
      {renderCompletedSection()}
    </div>
  );
};

export default AdvTaskUserTaskItem;
